import React, { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Save,
  AlertCircle,
  CheckCircle2,
  Circle,
  CircleDot,
  Loader2,
  Info,
  Sparkles
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@/components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { projectPageFormSchema, generateSlug, type ProjectPageFormValues } from '@/lib/validation/projectPage'
import type { GalleryImage, ProjectPage } from '@/types/projectPages'
import GalleryPreview from './GalleryPreview'
import GalleryImagesManager from './GalleryImagesManager'
import GallerySelect from './GallerySelect'
import { AIGenerationBadge } from './AIGenerationBadge'
import { useCheckSlugUnique } from '@/features/project-pages/hooks/useProjectPages'

interface ProjectPageFormProps {
  initialData?: ProjectPage
  onSubmit: (data: ProjectPageFormValues) => void
  isSubmitting?: boolean
}

type TabValue = 'basic' | 'seo' | 'project' | 'publishing'
type SectionStatus = 'pending' | 'active' | 'complete' | 'error'

type SectionConfig = {
  id: TabValue
  label: string
  description: string
  fields: (keyof ProjectPageFormValues)[]
  requiredFields: (keyof ProjectPageFormValues)[]
}

type FormGalleryImages = NonNullable<ProjectPageFormValues['gallery_images']>
type FormGalleryImage = FormGalleryImages[number]

const SECTION_CONFIG: SectionConfig[] = [
  {
    id: 'basic',
    label: 'Essentials',
    description: 'Gallery, title, and overview',
    fields: ['gallery_id', 'title', 'slug', 'description', 'hero_image_url', 'gallery_images', 'service_city', 'service_province', 'service_type'],
    requiredFields: ['gallery_id', 'title', 'slug']
  },
  {
    id: 'seo',
    label: 'SEO & Social',
    description: 'Discoverability and sharing',
    fields: ['seo_title', 'seo_description', 'keywords', 'canonical_url', 'og_title', 'og_description', 'og_image_url', 'twitter_title', 'twitter_description', 'twitter_image_url'],
    requiredFields: []
  },
  {
    id: 'project',
    label: 'Project Story',
    description: 'Details and testimonials',
    fields: ['completion_date', 'materials_used', 'price_range', 'testimonial_text', 'testimonial_author'],
    requiredFields: []
  },
  {
    id: 'publishing',
    label: 'Publishing',
    description: 'Status and visibility',
    fields: ['status', 'publish_at', 'featured', 'sort_order', 'tags'],
    requiredFields: ['status']
  }
]

const normalizeGalleryImages = (images?: ProjectPage['gallery_images']): FormGalleryImages => {
  if (!Array.isArray(images)) return []

  const rawImages = images as unknown[]

  return rawImages.map((entry, index): FormGalleryImage => {
    if (typeof entry === 'string') {
      return {
        url: entry,
        alt: `Gallery image ${index + 1}`,
        caption: undefined
      }
    }

    const typedEntry = entry as GalleryImage
    return {
      url: typedEntry.url,
      alt: typedEntry.alt || `Gallery image ${index + 1}`,
      caption: typedEntry.caption?.trim() || undefined
    }
  })
}

const OG_TYPES = ['website', 'article', 'profile'] as const
const TWITTER_CARD_TYPES = ['summary', 'summary_large_image', 'app', 'player'] as const
const PRICE_RANGES = [
  '$ · Under $1,000',
  '$$ · $1,000 - $5,000',
  '$$$ · $5,000 - $10,000',
  '$$$$ · $10,000 - $20,000',
  '$$$$$ · $20,000+'
] as const

const isOgType = (value: unknown): value is (typeof OG_TYPES)[number] =>
  OG_TYPES.includes(value as (typeof OG_TYPES)[number])

const isTwitterCardType = (value: unknown): value is (typeof TWITTER_CARD_TYPES)[number] =>
  TWITTER_CARD_TYPES.includes(value as (typeof TWITTER_CARD_TYPES)[number])

const isPriceRange = (value: unknown): value is (typeof PRICE_RANGES)[number] =>
  PRICE_RANGES.includes(value as (typeof PRICE_RANGES)[number])

/**
 * Enhanced Project Page Form with Editorial Aesthetic
 *
 * Design Philosophy: Refined editorial magazine layout with clear hierarchy,
 * smart defaults, and unobtrusive validation feedback.
 */
export default function ProjectPageForm({ initialData, onSubmit, isSubmitting = false }: ProjectPageFormProps) {
  const [activeTab, setActiveTab] = useState<TabValue>('basic')
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>(initialData ? 'available' : 'idle')
  const slugCheckMutation = useCheckSlugUnique()

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors, dirtyFields, touchedFields }
  } = useForm<ProjectPageFormValues>({
    resolver: zodResolver(projectPageFormSchema),
    defaultValues: initialData
      ? {
          gallery_id: initialData.gallery_id,
          slug: initialData.slug,
          title: initialData.title,
          status: initialData.status,
          description: initialData.description ?? undefined,
          hero_image_url: initialData.hero_image_url ?? undefined,
          gallery_images: normalizeGalleryImages(initialData.gallery_images || undefined),
          service_city: initialData.service_city ?? undefined,
          service_province: initialData.service_province ?? undefined,
          service_type: initialData.service_type ?? undefined,
          seo_title: initialData.seo_title ?? undefined,
          seo_description: initialData.seo_description ?? undefined,
          keywords: initialData.keywords || [],
          canonical_url: initialData.canonical_url ?? undefined,
          schema_json: initialData.schema_json ?? undefined,
          og_title: initialData.og_title ?? undefined,
          og_description: initialData.og_description ?? undefined,
          og_image_url: initialData.og_image_url ?? undefined,
          og_type: isOgType(initialData.og_type) ? initialData.og_type : 'website',
          twitter_card_type: isTwitterCardType(initialData.twitter_card_type) ? initialData.twitter_card_type : 'summary_large_image',
          twitter_title: initialData.twitter_title ?? undefined,
          twitter_description: initialData.twitter_description ?? undefined,
          twitter_image_url: initialData.twitter_image_url ?? undefined,
          publish_at: initialData.publish_at ?? undefined,
          featured: Boolean(initialData.featured),
          sort_order: initialData.sort_order ?? 0,
          tags: initialData.tags || [],
          completion_date: initialData.completion_date ?? undefined,
          materials_used: initialData.materials_used || [],
          price_range: isPriceRange(initialData.price_range) ? initialData.price_range : undefined,
          testimonial_text: initialData.testimonial_text ?? undefined,
          testimonial_author: initialData.testimonial_author ?? undefined,
          related_project_ids: initialData.related_project_ids || []
        }
      : {
          status: 'draft',
          featured: false,
          sort_order: 0,
          keywords: [],
          tags: [],
          gallery_id: undefined,
          gallery_images: [],
          materials_used: [],
          og_type: 'website',
          twitter_card_type: 'summary_large_image',
          related_project_ids: []
        }
  })

  const titleValue = watch('title')
  const slugValue = watch('slug')
  const galleryIdValue = watch('gallery_id')
  const statusValue = watch('status')

  useEffect(() => {
    if (!initialData && titleValue && !slugValue) {
      const autoSlug = generateSlug(titleValue)
      setValue('slug', autoSlug)
    }
  }, [titleValue, slugValue, initialData, setValue])

  useEffect(() => {
    if (!slugValue) {
      if (slugStatus !== 'idle') setSlugStatus('idle')
      return
    }

    if (initialData && slugValue === initialData.slug) {
      if (slugStatus !== 'available') setSlugStatus('available')
      return
    }

    if (slugStatus === 'available') {
      setSlugStatus('idle')
    }
  }, [slugValue, initialData, slugStatus])

  const handleSlugBlur = async () => {
    const slug = slugValue?.trim()
    if (!slug) {
      setSlugStatus('idle')
      return
    }

    if (initialData && slug === initialData.slug) {
      setSlugStatus('available')
      return
    }

    setSlugStatus('checking')
    try {
      const result = await slugCheckMutation.mutateAsync({ slug, excludeId: initialData?.id })
      setSlugStatus(result.isUnique ? 'available' : 'taken')
    } catch (error) {
      console.error('Failed to check slug', error)
      setSlugStatus('idle')
    }
  }

  const sectionStatuses = useMemo(() => {
    return SECTION_CONFIG.map((section) => {
      const hasError = section.fields.some((field) => Boolean(errors[field]))
      const hasRequiredError = section.requiredFields.some((field) => Boolean(errors[field]))
      const isDirty = section.fields.some((field) => Boolean(dirtyFields[field]))

      let status: SectionStatus = 'pending'
      if (hasRequiredError) status = 'error'
      else if (activeTab === section.id) status = 'active'
      else if (isDirty || section.requiredFields.every((field) => !errors[field])) status = 'complete'

      return { ...section, status, hasError }
    })
  }, [activeTab, errors, dirtyFields])

  const galleryImagesError = Array.isArray(errors.gallery_images) ? undefined : errors.gallery_images?.message
  const errorCount = Object.keys(errors).length

  // Debug: Log errors to console for troubleshooting
  useEffect(() => {
    if (errorCount > 0) {
      console.log('Form validation errors:', errors)
    }
  }, [errors, errorCount])

  // Calculate completion percentage
  const completionPercentage = useMemo(() => {
    const requiredFieldsCount = SECTION_CONFIG.reduce((acc, section) => acc + section.requiredFields.length, 0)
    const completedRequiredFields = SECTION_CONFIG.reduce((acc, section) => {
      const completed = section.requiredFields.filter((field) => !errors[field] && touchedFields[field])
      return acc + completed.length
    }, 0)
    return Math.round((completedRequiredFields / requiredFieldsCount) * 100)
  }, [errors, touchedFields])

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="relative">
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }

        .animate-pulse-slow {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        /* Editorial typography */
        .editorial-title {
          font-family: 'Georgia', 'Times New Roman', serif;
          letter-spacing: -0.02em;
          font-weight: 500;
        }

        .editorial-label {
          font-family: 'Helvetica Neue', 'Arial', sans-serif;
          font-size: 0.6875rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          font-weight: 600;
        }

        /* Refined card styling */
        .editorial-card {
          border: 1px solid rgba(0, 0, 0, 0.06);
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
          transition: all 0.2s ease;
        }

        .editorial-card:hover {
          border-color: rgba(0, 0, 0, 0.12);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
        }

        /* Status indicator styles */
        .status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          display: inline-block;
          margin-right: 8px;
        }

        .status-dot.draft { background: #94a3b8; }
        .status-dot.published { background: #22c55e; }
        .status-dot.archived { background: #f59e0b; }
      `}</style>

      {/* AI Generation Badge - Show which API was used */}
      {initialData?.generation_metadata && (
        <div className="mb-6 flex items-center justify-between bg-muted/50 rounded-lg border px-4 py-3">
          <div className="flex items-center gap-3">
            <Sparkles className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">AI-generated content</span>
          </div>
          <AIGenerationBadge metadata={initialData.generation_metadata} />
        </div>
      )}

      {/* Top Alert Bar - Show specific validation errors */}
      {errorCount > 0 && (
        <Alert variant="destructive" className="mb-6 animate-slide-in border-l-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-3">
              <div className="font-medium">
                {errorCount} validation {errorCount === 1 ? 'issue' : 'issues'} found - fix these to save:
              </div>
              <ul className="text-sm space-y-1.5 pl-4">
                {Object.entries(errors).map(([field, error]) => {
                  // Handle nested errors (like gallery_images array)
                  const errorMessage =
                    typeof (error as any)?.message === 'string'
                      ? (error as any).message
                      : typeof (error as any)?.root?.message === 'string'
                        ? (error as any).root.message
                        : 'Invalid value'
                  const fieldLabel = field
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, (l) => l.toUpperCase())

                  return (
                    <li key={field} className="flex items-start gap-2">
                      <span className="text-red-300">→</span>
                      <div>
                        <span className="font-semibold">{fieldLabel}</span>: {errorMessage}
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <div className="lg:grid lg:grid-cols-[240px_1fr] lg:gap-8">
        {/* Sidebar Navigation */}
        <div className="hidden lg:block">
          <div className="sticky top-24 space-y-6">
            {/* Progress Card */}
            <Card className="editorial-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm editorial-label">Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Required fields</span>
                    <span className="font-semibold">{completionPercentage}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500"
                      style={{ width: `${completionPercentage}%` }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section Navigation */}
            <Card className="editorial-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm editorial-label">Sections</CardTitle>
                <CardDescription className="text-xs">Navigate and track completion</CardDescription>
              </CardHeader>
              <CardContent className="space-y-1">
                {sectionStatuses.map((section) => (
                  <button
                    type="button"
                    key={section.id}
                    onClick={() => setActiveTab(section.id)}
                    className={cn(
                      'flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-all',
                      section.id === activeTab
                        ? 'bg-blue-50 text-blue-900 border border-blue-200'
                        : 'hover:bg-gray-50 border border-transparent'
                    )}
                  >
                    <div className="mt-0.5">
                      {section.status === 'complete' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                      {section.status === 'active' && <CircleDot className="h-4 w-4 text-blue-600" />}
                      {section.status === 'pending' && <Circle className="h-4 w-4 text-gray-400" />}
                      {section.status === 'error' && <AlertCircle className="h-4 w-4 text-red-600 animate-pulse-slow" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium leading-tight">{section.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{section.description}</p>
                      {section.hasError && (
                        <p className="text-xs text-red-600 mt-1">Needs attention</p>
                      )}
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>

            {/* Status Preview */}
            {statusValue && (
              <Card className="editorial-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm editorial-label">Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center text-sm">
                    <span className={`status-dot ${statusValue}`}></span>
                    <span className="capitalize font-medium">{statusValue}</span>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="space-y-6">
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as TabValue)} className="w-full">
            <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto p-1 bg-gray-100">
              {SECTION_CONFIG.map((section) => (
                <TabsTrigger
                  key={section.id}
                  value={section.id}
                  className="data-[state=active]:bg-white data-[state=active]:shadow-sm py-2.5"
                >
                  <span className="hidden sm:inline">{section.label}</span>
                  <span className="sm:hidden text-xs">{section.label.split(' ')[0]}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {/* BASIC INFO TAB */}
            <TabsContent value="basic" className="space-y-6 mt-6">
              <Card className="editorial-card">
                <CardHeader>
                  <CardTitle className="editorial-title text-2xl">Essential Information</CardTitle>
                  <CardDescription>Core details that define your project page</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Gallery Selection - REQUIRED */}
                  <div className="space-y-2">
                    <Label htmlFor="gallery_id" className="editorial-label flex items-center gap-2">
                      <span className="text-red-600">●</span> Gallery
                    </Label>
                    <Controller
                      name="gallery_id"
                      control={control}
                      render={({ field }) => (
                        <GallerySelect
                          value={field.value as number | undefined}
                          onChange={(val) => field.onChange(val ?? undefined)}
                          disabled={!!initialData}
                        />
                      )}
                    />
                    {errors.gallery_id && (
                      <p className="text-sm text-red-600 flex items-center gap-1 animate-slide-in">
                        <AlertCircle className="h-3.5 w-3.5" />
                        {errors.gallery_id.message}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Info className="h-3.5 w-3.5" />
                      {initialData ? 'Gallery selection is locked after creation' : 'Select the gallery containing project imagery'}
                    </p>
                  </div>

                  {galleryIdValue && galleryIdValue > 0 && (
                    <div className="animate-slide-in">
                      <GalleryPreview galleryId={galleryIdValue} />
                    </div>
                  )}

                  {/* Title - REQUIRED */}
                  <div className="space-y-2">
                    <Label htmlFor="title" className="editorial-label flex items-center gap-2">
                      <span className="text-red-600">●</span> Title
                    </Label>
                    <Input
                      id="title"
                      {...register('title')}
                      placeholder="e.g., Historic Brick Restoration in Downtown Toronto"
                      className="text-lg"
                    />
                    {errors.title && (
                      <p className="text-sm text-red-600 flex items-center gap-1 animate-slide-in">
                        <AlertCircle className="h-3.5 w-3.5" />
                        {errors.title.message}
                      </p>
                    )}
                  </div>

                  {/* Slug - REQUIRED */}
                  <div className="space-y-2">
                    <Label htmlFor="slug" className="editorial-label flex items-center gap-2">
                      <span className="text-red-600">●</span> URL Slug
                    </Label>
                    <div className="flex items-center gap-2 bg-gray-50 rounded-md px-3 py-1">
                      <span className="text-sm text-muted-foreground font-mono">/projects/</span>
                      <Input
                        id="slug"
                        {...register('slug')}
                        onBlur={handleSlugBlur}
                        placeholder="historic-brick-restoration-toronto"
                        className="font-mono bg-transparent border-0 focus-visible:ring-0 px-0"
                      />
                    </div>
                    {errors.slug && (
                      <p className="text-sm text-red-600 flex items-center gap-1 animate-slide-in">
                        <AlertCircle className="h-3.5 w-3.5" />
                        {errors.slug.message}
                      </p>
                    )}
                    {slugStatus === 'checking' && (
                      <p className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Checking availability...
                      </p>
                    )}
                    {slugStatus === 'available' && (
                      <p className="flex items-center gap-1 text-sm text-green-700">
                        <CheckCircle2 className="h-4 w-4" /> Available
                      </p>
                    )}
                    {slugStatus === 'taken' && (
                      <p className="flex items-center gap-1 text-sm text-red-600">
                        <AlertCircle className="h-4 w-4" /> Already in use
                      </p>
                    )}
                  </div>

                  {/* Description - Optional */}
                  <div className="space-y-2">
                    <Label htmlFor="description" className="editorial-label">Description (Optional)</Label>
                    <Textarea
                      id="description"
                      {...register('description')}
                      placeholder="A compelling overview of the project that highlights the challenge, solution, and results..."
                      rows={6}
                      className="resize-none"
                    />
                    {errors.description && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-3.5 w-3.5" />
                        {errors.description.message}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">This appears as the main project description</p>
                  </div>

                  {/* Hero Image - Optional */}
                  <div className="space-y-2">
                    <Label htmlFor="hero_image_url" className="editorial-label">Hero Image URL (Optional)</Label>
                    <Input
                      id="hero_image_url"
                      {...register('hero_image_url')}
                      placeholder="https://..."
                      type="url"
                    />
                    {errors.hero_image_url && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-3.5 w-3.5" />
                        {errors.hero_image_url.message}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">Featured image displayed at the top of the page</p>
                  </div>

                  {/* Custom Gallery Images - Optional */}
                  <div className="space-y-2 pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <Label className="editorial-label flex items-center gap-2">
                        <Sparkles className="h-3.5 w-3.5" />
                        Custom Gallery Images (Optional)
                      </Label>
                      <span className="text-xs text-muted-foreground">Curated selections</span>
                    </div>
                    <Controller
                      name="gallery_images"
                      control={control}
                      render={({ field }) => (
                        <GalleryImagesManager
                          value={(field.value as GalleryImage[]) || []}
                          onChange={field.onChange}
                        />
                      )}
                    />
                    {/* Show gallery images errors - handle both array and individual errors */}
                    {errors.gallery_images && (
                      <div className="space-y-2 p-3 bg-red-50 border border-red-200 rounded-md">
                        {galleryImagesError && (
                          <p className="text-sm text-red-600 flex items-center gap-1 font-medium">
                            <AlertCircle className="h-4 w-4" />
                            {galleryImagesError}
                          </p>
                        )}
                        {Array.isArray(errors.gallery_images) && errors.gallery_images.map((imgError, idx) => (
                          imgError && (
                            <p key={idx} className="text-sm text-red-600 pl-5">
                              Image #{idx + 1}: {imgError.alt?.message || imgError.url?.message || 'Invalid'}
                            </p>
                          )
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Supplement the main gallery with handpicked highlights. Each image requires alt text for accessibility.
                    </p>
                  </div>

                  {/* Location Fields */}
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                    <div className="space-y-2">
                      <Label htmlFor="service_city" className="editorial-label">City (Optional)</Label>
                      <Input
                        id="service_city"
                        {...register('service_city')}
                        placeholder="Toronto"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="service_province" className="editorial-label">Province (Optional)</Label>
                      <Input
                        id="service_province"
                        {...register('service_province')}
                        placeholder="Ontario"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="service_type" className="editorial-label">Service Type (Optional)</Label>
                    <Input
                      id="service_type"
                      {...register('service_type')}
                      placeholder="e.g., Brick Restoration, Chimney Repair, Tuckpointing"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* SEO TAB - All Optional */}
            <TabsContent value="seo" className="space-y-6 mt-6">
              <Card className="editorial-card">
                <CardHeader>
                  <CardTitle className="editorial-title text-2xl">Search Engine Optimization</CardTitle>
                  <CardDescription>Help search engines understand and rank your content</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="seo_title" className="editorial-label">SEO Title (Optional)</Label>
                    <Input
                      id="seo_title"
                      {...register('seo_title')}
                      placeholder="Override default title for search results"
                    />
                    <p className="text-xs text-muted-foreground">Recommended: 50-60 characters for optimal display</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="seo_description" className="editorial-label">SEO Description (Optional)</Label>
                    <Textarea
                      id="seo_description"
                      {...register('seo_description')}
                      placeholder="Compelling description that appears in search results"
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">Recommended: 150-160 characters</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="keywords" className="editorial-label">Keywords (Optional)</Label>
                    <Input
                      id="keywords"
                      {...register('keywords')}
                      placeholder="brick restoration, masonry, toronto, heritage"
                    />
                    <p className="text-xs text-muted-foreground">Comma-separated relevant keywords</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="canonical_url" className="editorial-label">Canonical URL (Optional)</Label>
                    <Input
                      id="canonical_url"
                      {...register('canonical_url')}
                      placeholder="https://fixmybrick.ca/projects/..."
                      type="url"
                    />
                    <p className="text-xs text-muted-foreground">Prevent duplicate content issues</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="editorial-card">
                <CardHeader>
                  <CardTitle className="editorial-title text-xl">Social Media Preview</CardTitle>
                  <CardDescription>Control how your page appears when shared</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Open Graph */}
                  <div className="space-y-4">
                    <h4 className="editorial-label text-sm">Facebook / Open Graph</h4>
                    <div className="space-y-2">
                      <Label htmlFor="og_title" className="text-xs">Title (Optional)</Label>
                      <Input id="og_title" {...register('og_title')} placeholder="Social share title" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="og_description" className="text-xs">Description (Optional)</Label>
                      <Textarea
                        id="og_description"
                        {...register('og_description')}
                        placeholder="Engaging description for social shares"
                        rows={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="og_image_url" className="text-xs">Image URL (Optional)</Label>
                      <Input
                        id="og_image_url"
                        {...register('og_image_url')}
                        placeholder="https://..."
                        type="url"
                      />
                      <p className="text-xs text-muted-foreground">Recommended: 1200×630 pixels</p>
                    </div>
                  </div>

                  {/* Twitter */}
                  <div className="space-y-4 pt-4 border-t">
                    <h4 className="editorial-label text-sm">Twitter / X</h4>
                    <div className="space-y-2">
                      <Label htmlFor="twitter_title" className="text-xs">Title (Optional)</Label>
                      <Input
                        id="twitter_title"
                        {...register('twitter_title')}
                        placeholder="Social share title"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="twitter_description" className="text-xs">Description (Optional)</Label>
                      <Textarea
                        id="twitter_description"
                        {...register('twitter_description')}
                        placeholder="Engaging description for Twitter shares"
                        rows={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="twitter_image_url" className="text-xs">Image URL (Optional)</Label>
                      <Input
                        id="twitter_image_url"
                        {...register('twitter_image_url')}
                        placeholder="https://..."
                        type="url"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* PROJECT DETAILS TAB - All Optional */}
            <TabsContent value="project" className="space-y-6 mt-6">
              <Card className="editorial-card">
                <CardHeader>
                  <CardTitle className="editorial-title text-2xl">Project Details</CardTitle>
                  <CardDescription>Key project information from Jobber data</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="completion_date" className="editorial-label">Completion Date (Optional)</Label>
                      <Input
                        id="completion_date"
                        {...register('completion_date')}
                        type="date"
                      />
                      <p className="text-xs text-muted-foreground">From Jobber job.completedAt</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="price_range" className="editorial-label">Price Range (Optional)</Label>
                      <Controller
                        name="price_range"
                        control={control}
                        render={({ field }) => (
                          <Select value={field.value || ''} onValueChange={(val) => field.onChange(val || undefined)}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select price tier" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="$ · Under $1,000">
                                <span className="font-medium">$</span> · Under $1,000
                              </SelectItem>
                              <SelectItem value="$$ · $1,000 - $5,000">
                                <span className="font-medium">$$</span> · $1,000 - $5,000
                              </SelectItem>
                              <SelectItem value="$$$ · $5,000 - $10,000">
                                <span className="font-medium">$$$</span> · $5,000 - $10,000
                              </SelectItem>
                              <SelectItem value="$$$$ · $10,000 - $20,000">
                                <span className="font-medium">$$$$</span> · $10,000 - $20,000
                              </SelectItem>
                              <SelectItem value="$$$$$ · $20,000+">
                                <span className="font-medium">$$$$$</span> · $20,000+
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                      <p className="text-xs text-muted-foreground">Based on invoice total from Jobber</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="materials_used" className="editorial-label">Materials Used (Optional)</Label>
                    <Input
                      id="materials_used"
                      {...register('materials_used')}
                      placeholder="Red brick, Type N mortar, limestone, etc."
                    />
                    <p className="text-xs text-muted-foreground">Comma-separated list extracted from line item descriptions</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="editorial-card">
                <CardHeader>
                  <CardTitle className="editorial-title text-xl">Customer Testimonial</CardTitle>
                  <CardDescription>Share client feedback and satisfaction</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="testimonial_text" className="editorial-label">Testimonial (Optional)</Label>
                    <Textarea
                      id="testimonial_text"
                      {...register('testimonial_text')}
                      placeholder="What did the customer say about their experience?"
                      rows={4}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="testimonial_author" className="editorial-label">Customer Name (Optional)</Label>
                    <Input
                      id="testimonial_author"
                      {...register('testimonial_author')}
                      placeholder="e.g., John Smith"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* PUBLISHING TAB */}
            <TabsContent value="publishing" className="space-y-6 mt-6">
              <Card className="editorial-card">
                <CardHeader>
                  <CardTitle className="editorial-title text-2xl">Publishing Controls</CardTitle>
                  <CardDescription>Manage visibility, scheduling, and organization</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Status - REQUIRED */}
                  <div className="space-y-2">
                    <Label htmlFor="status" className="editorial-label flex items-center gap-2">
                      <span className="text-red-600">●</span> Status
                    </Label>
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select publication status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft">
                              <div className="flex items-center gap-2">
                                <span className="status-dot draft"></span>
                                <div>
                                  <div className="font-medium">Draft</div>
                                  <div className="text-xs text-muted-foreground">Work in progress, not visible</div>
                                </div>
                              </div>
                            </SelectItem>
                            <SelectItem value="published">
                              <div className="flex items-center gap-2">
                                <span className="status-dot published"></span>
                                <div>
                                  <div className="font-medium">Published</div>
                                  <div className="text-xs text-muted-foreground">Live and visible to public</div>
                                </div>
                              </div>
                            </SelectItem>
                            <SelectItem value="archived">
                              <div className="flex items-center gap-2">
                                <span className="status-dot archived"></span>
                                <div>
                                  <div className="font-medium">Archived</div>
                                  <div className="text-xs text-muted-foreground">Hidden but preserved</div>
                                </div>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.status && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-3.5 w-3.5" />
                        {errors.status.message}
                      </p>
                    )}
                  </div>

                  {/* Publish Date - Optional */}
                  <div className="space-y-2">
                    <Label htmlFor="publish_at" className="editorial-label">Publish Date & Time (Optional)</Label>
                    <Input
                      id="publish_at"
                      {...register('publish_at')}
                      type="datetime-local"
                    />
                    <p className="text-xs text-muted-foreground">Leave empty to publish immediately when status changes to Published</p>
                  </div>

                  {/* Featured Toggle */}
                  <div className="flex items-start justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <div className="flex-1">
                      <Label htmlFor="featured" className="editorial-label cursor-pointer">Featured Project</Label>
                      <p className="text-sm text-muted-foreground mt-1">Highlight this project on the homepage and in featured collections</p>
                    </div>
                    <Controller
                      name="featured"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          id="featured"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          className="mt-1"
                        />
                      )}
                    />
                  </div>

                  {/* Sort Order */}
                  <div className="space-y-2">
                    <Label htmlFor="sort_order" className="editorial-label">Sort Order (Optional)</Label>
                    <Input
                      id="sort_order"
                      {...register('sort_order', { valueAsNumber: true })}
                      type="number"
                      min="0"
                      className="w-32"
                    />
                    <p className="text-xs text-muted-foreground">Lower numbers appear first in listings (0 = highest priority)</p>
                  </div>

                  {/* Tags */}
                  <div className="space-y-2">
                    <Label htmlFor="tags" className="editorial-label">Tags (Optional)</Label>
                    <Input
                      id="tags"
                      {...register('tags')}
                      placeholder="residential, commercial, heritage, restoration"
                    />
                    <p className="text-xs text-muted-foreground">Comma-separated tags for filtering and organization</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Form Actions */}
          <div className="flex items-center justify-between gap-4 pt-6 border-t">
            <div className="text-sm text-muted-foreground">
              {errorCount > 0 ? (
                <span className="flex items-center gap-1 text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  {errorCount} validation {errorCount === 1 ? 'issue' : 'issues'}
                </span>
              ) : (
                <span className="flex items-center gap-1 text-green-700">
                  <CheckCircle2 className="h-4 w-4" />
                  All required fields complete
                </span>
              )}
            </div>
            <Button
              type="submit"
              disabled={isSubmitting || errorCount > 0}
              size="lg"
              className="min-w-[160px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {initialData ? 'Update' : 'Create'} Project Page
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </form>
  )
}
